import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Paper,
  Skeleton,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { productsApi, Product } from '../services/api';
import { cartService } from '../services/cart';

const ProductDetail: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [addToCartMessage, setAddToCartMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await productsApi.getById(Number(productId));
        
        if (response?.data?.data?.product) {
          const productData = response.data.data.product;
          const formattedProduct = {
            ...productData,
            price: typeof productData.price === 'string' ? parseFloat(productData.price) : productData.price,
            cost: typeof productData.cost === 'string' ? parseFloat(productData.cost) : productData.cost
          };
          setProduct(formattedProduct);
          setSelectedImage(getImageUrl(productData.imageUrl));
        } else {
          setError('Product not found or invalid data format');
        }
      } catch (err) {
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId]);

  const getImageUrl = (imageUrl: string | undefined | null) => {
    const defaultPlaceholder = 'https://placehold.co/400x400?text=No+Image';
    
    try {
      if (!imageUrl) return defaultPlaceholder;

      // If it's an S3 URL, return it directly
      if (imageUrl.startsWith('https://skylight-image-storage.s3.amazonaws.com')) {
        return imageUrl;
      }

      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://skylightbackend-production.up.railway.app';
      
      if (imageUrl.startsWith('http')) {
        const url = new URL(imageUrl);
        if (url.hostname === 'skylightbackend-production.up.railway.app') {
          return imageUrl;
        }
      }

      const cleanUrl = imageUrl
        .replace(/['"]/g, '')
        .replace(/;/g, '')
        .replace(/^\/+/, '')
        .replace(/\/+/g, '/')
        .trim();

      let filename = cleanUrl;
      
      const patterns = [
        /^uploads\/products\/(.+)$/,
        /^products\/(.+)$/,
        /^app\/uploads\/products\/(.+)$/,
        /^(.+)$/
      ];

      for (const pattern of patterns) {
        const match = cleanUrl.match(pattern);
        if (match && match[1]) {
          filename = match[1];
          break;
        }
      }

      filename = filename
        .replace(/[^\w\-.]/g, '')
        .replace(/\.+/g, '.')
        .toLowerCase();

      if (!filename || filename.length < 5) return defaultPlaceholder;

      const finalUrl = `${apiBaseUrl}/uploads/products/${filename}`;

      try {
        new URL(finalUrl);
        return finalUrl;
      } catch {
        return defaultPlaceholder;
      }
    } catch {
      return defaultPlaceholder;
    }
  };

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const defaultPlaceholder = 'https://placehold.co/400x400?text=No+Image';
    if (!img.src.includes('placehold.co')) {
      img.src = defaultPlaceholder;
    }
  };

  const calculateDiscount = (originalPrice: number, currentPrice: number) => {
    if (typeof originalPrice !== 'number' || typeof currentPrice !== 'number') {
      return 0;
    }
    if (originalPrice > currentPrice) {
      const discount = ((originalPrice - currentPrice) / originalPrice) * 100;
      return Math.round(discount);
    }
    return 0;
  };

  const formatPrice = (price: number | string | undefined): string => {
    if (price === undefined || price === null) return '0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return !isNaN(numPrice) ? numPrice.toFixed(2) : '0.00';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 2 }}>
          <Skeleton variant="rectangular" width={40} height={40} />
        </Box>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" width="100%" height={400} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={30} />
            <Skeleton variant="text" height={30} />
            <Box sx={{ mt: 2 }}>
              <Skeleton variant="rectangular" width={200} height={50} />
            </Box>
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography color="error" variant="h6" gutterBottom>
          {error || 'Product not found'}
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
          variant="contained"
          color="primary"
        >
          Go Back to Products
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
        variant="outlined"
        color="primary"
      >
        Back to Products
      </Button>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: 'background.paper',
              borderRadius: 2,
              overflow: 'hidden',
              transition: 'transform 0.3s ease-in-out',
              '&:hover': {
                transform: 'scale(1.02)',
              },
            }}
          >
            <Box
              component="img"
              src={selectedImage}
              alt={product.name}
              onError={handleImageError}
              sx={{
                width: '100%',
                maxHeight: { xs: 300, md: 400 },
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Box>
            <Typography
              variant={isMobile ? 'h5' : 'h4'}
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                mb: 2,
              }}
            >
              {product.name}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h5"
                component="p"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.success.main,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                MRP: ₹{formatPrice(product.price)}
                {product.cost > 0 && product.cost > product.price && (
                  <Chip
                    label={`${calculateDiscount(product.cost, product.price)}% OFF`}
                    color="error"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              {product.cost > 0 && product.cost > product.price && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'line-through',
                    mt: 0.5,
                  }}
                >
                  Original MRP: ₹{formatPrice(product.cost)}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography
              variant="body1"
              sx={{
                color: 'text.primary',
                mb: 3,
                lineHeight: 1.8,
              }}
            >
              {product.description}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: product.stock > 0 ? 'success.main' : 'error.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {product.stock > 0 ? 'In Stock' : 'Out of Stock'}
                <Chip
                  label={product.stock > 0 ? `${product.stock} available` : 'Out of Stock'}
                  color={product.stock > 0 ? 'success' : 'error'}
                  size="small"
                />
              </Typography>
            </Box>

            <Box sx={{ mt: 4 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                Product Details
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                Category: {product.Category?.name || 'N/A'}
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 3, minWidth: 180 }}
              disabled={product.stock <= 0}
              onClick={() => {
                if (product) {
                  cartService.addToCart(product, 1);
                  setAddToCartMessage('Added to cart!');
                  setTimeout(() => setAddToCartMessage(null), 1500);
                }
              }}
            >
              Add to Cart
            </Button>
            {addToCartMessage && (
              <Typography color="success.main" sx={{ mt: 1 }}>
                {addToCartMessage}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProductDetail; 